import {
  QueryConstraint,
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  arrayUnion,
  where,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import {
  type Imaging,
  type ImagingNotificationQueue,
  type ImagingShortcut,
  type ImagingTrackingNotification,
  type ImagingsFilters,
  type TemporaryAccessResult,
} from "@/features/imagings/schemas/imagings";
import { http } from "../../../apis";
import type { ApiResponse } from "../../../shared";

const imagingsCollection = collection(db, "imagings");
const trackingNotificationsCollection = collection(db, "tracking_notifications");

/**
 * Función para convertir string de fecha de Firebase a Date
 * @param firebaseDateString - String de fecha de Firebase (ej: "2024-12-31 19:33:15")
 * @returns {Date} Objeto Date
 */
function parseFirebaseDate(firebaseDateString: string): Date {
  try {
    // Firebase stores datetime as "2024-12-31 19:33:15"
    // We need to convert this to a proper ISO string for Date constructor
    if (firebaseDateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      // Convert "2024-12-31 19:33:15" to "2024-12-31T19:33:15.000Z"
      const isoString = firebaseDateString.replace(" ", "T") + ".000Z";
      return new Date(isoString);
    }

    // Fallback: try to parse as is
    const date = new Date(firebaseDateString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    console.error("Error parsing Firebase date:", firebaseDateString);
    return new Date();
  } catch (error) {
    console.error("Error parsing Firebase date:", firebaseDateString, error);
    return new Date();
  }
}

/**
 * Función para convertir datos de Firestore a Imagings
 * @param data - Los datos del documento de Firestore
 * @returns {Imagings} Los datos convertidos
 */
function convertFirestoreData(data: Record<string, unknown>, id: string): Imaging {
  return {
    ...data,
    // Convert Firebase datetime string to Date object
    datetime:
      typeof data.datetime === "string"
        ? parseFirebaseDate(data.datetime)
        : data.datetime instanceof Date
          ? data.datetime
          : (data.datetime as { toDate?: () => Date }).toDate?.() || new Date(),
    uid: id || data.uid,
  } as Imaging;
}

/**
 * Función para obtener imágenes filtradas con mejor manejo de fechas
 * @param {ImagingsFilters} filters - Los filtros a aplicar
 * @returns {Promise<Imagings[]>} Las imágenes obtenidas
 */
export const getImagingsFiltered = async (
  filters: ImagingsFilters
): Promise<Imaging[]> => {
  try {
    const constraints: QueryConstraint[] = [];

    if (filters.institution_id) {
      constraints.push(where("institution_id", "==", filters.institution_id));
    }

    if (filters.startDate) {
      constraints.push(where("date_only", ">=", filters.startDate.split("T")[0]));
    }

    if (filters.endDate) {
      constraints.push(where("date_only", "<=", filters.endDate.split("T")[0]));
    }

    if (filters.patient_id) {
      constraints.push(where("patient_id", "==", filters.patient_id));
    }

    if (filters.accession_number) {
      constraints.push(where("accession_number", "==", filters.accession_number));
    }

    const querySnapshot = await getDocs(query(imagingsCollection, ...constraints));

    const imagings = querySnapshot.docs.map((doc) =>
      convertFirestoreData(doc.data(), doc.id)
    );

    return imagings;
  } catch (error) {
    console.error("❌ Error in getImagingsFiltered:", error);
    throw new Error(
      `Error al obtener imágenes filtradas: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Función para obtener una imagen específica por ID
 * @param {string} imagingId - El ID de la imagen
 * @returns {Promise<Imagings>} La imagen obtenida
 */
export const getImagingById = async (imagingId: string): Promise<Imaging> => {
  try {
    const imagingDoc = doc(imagingsCollection, imagingId);
    const imagingSnapshot = await getDoc(imagingDoc);

    if (!imagingSnapshot.exists()) {
      throw new Error("Imagen no encontrada");
    }

    return convertFirestoreData(imagingSnapshot.data(), imagingSnapshot.id);
  } catch (error) {
    throw new Error(
      `Error al obtener la imagen: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Crea un shortcut en la base de datos
 * @param {Omit<Shortcut, "created_at" | "expired_at"> & { durationToken: number }} data - Los datos del shortcut
 * @returns {Promise<string>} El ID del shortcut creado
 */
export const createImagingShortcut = async (
  data: Omit<ImagingShortcut, "created_at" | "expired_at">
): Promise<string> => {
  try {
    const shortcutData = {
      ...data,
      expired_at: new Timestamp(
        Math.floor(Date.now() / 1000) + data.durationToken * 24 * 60 * 60,
        0
      ),
      created_at: new Timestamp(Math.floor(Date.now() / 1000), 0),
    };

    const docRef = await addDoc(collection(db, "shortcuts"), shortcutData);
    return docRef.id;
  } catch (error) {
    throw new Error(
      `Error al crear el shortcut: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Guarda una notificación de seguimiento en la base de datos
 * @param {Omit<ImagingTrackingNotification, "created_at">} data - Los datos de la notificación
 * @returns {Promise<string>} El ID de la notificación guardada
 */
export const saveImagingTrackingNotification = async (
  data: Omit<ImagingTrackingNotification, "created_at">
): Promise<string> => {
  try {
    const trackingData = {
      ...data,
      created_at: new Date(),
    };

    const docRef = await addDoc(collection(db, "tracking_notifications"), trackingData);
    return docRef.id;
  } catch (error) {
    throw new Error(
      `Error al guardar la notificación de seguimiento: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Obtiene datos de personas a las que se les ha compartido una imagen
 * @param {string} imagingIuid - El ID de la imagen
 * @returns {Promise<Imaging[]>} Los datos de las personas a las que se les ha compartido la imagen
 */
export const getImagingTrackingNotifications = async (
  imagingIuid: string
): Promise<ImagingTrackingNotification[]> => {
  try {
    const q = query(trackingNotificationsCollection, where("imaging", "==", imagingIuid));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => doc.data() as ImagingTrackingNotification);
  } catch (error) {
    throw new Error(
      `Error al obtener la notificación de seguimiento: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Guarda una notificación en la cola de notificaciones
 * @param {Object} data - Los datos de la notificación
 * @returns {Promise<string>} El ID de la notificación guardada
 */
export const savImagingNotificationQueue = async (
  data: ImagingNotificationQueue
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, "notification_queue"), data);
    return docRef.id;
  } catch (error) {
    throw new Error(
      `Error al guardar la notificación en la cola de notificaciones: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

//get prefix dicom
export const getPrefixDicom = async (user_id: string): Promise<string> => {
  const response = await http.get<ApiResponse<string>>(`/api/v1/imaging/uid/${user_id}`);
  return response.data;
};

/**
 * Guarda un resultado de acceso temporal en la base de datos
 * @param {TemporaryAccessResult} data - Los datos a guardar
 * @returns {Promise<string>} El ID del documento guardado
 */
export const saveTemporaryAccessResult = async (
  data: TemporaryAccessResult
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, "temporary_access_results"), data);
    return docRef.id;
  } catch (error) {
    throw new Error(
      `Error al guardar en temporary_access_results: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};

/**
 * Guarda un registro de estudio compartido en la base de datos y actualiza el array shared en imagings
 * @param {SharedStudyRecord} data - Los datos del registro compartido
 * @returns {Promise<string>} El ID del documento guardado
 */
export const saveSharedStudyRecord = async (
  data: import("@/features/imagings/schemas/imagings").SharedStudyRecord
): Promise<string> => {
  try {
    // 1. Guardar el nuevo registro en shared_studies
    const docRef = await addDoc(collection(db, "shared_studies"), data);

    // 2. Actualizar el documento en imagings agregando el correo al array `shared` sin duplicados
    const imagingDocRef = doc(db, "imagings", data.imaging);
    await updateDoc(imagingDocRef, {
      shared: arrayUnion(data.user_receiving)
    });

    return docRef.id;
  } catch (error) {
    throw new Error(
      `Error al guardar en shared_studies y actualizar imaging: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }
};
